import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface QueueEntry {
  id: string;
  queue_number: string | number;
  pet_name: string;
  owner_name?: string;
  priority: string;
  status: string;
  wait_time?: number;
  vet_name?: string;
  queue_id?: string;
  queue_name?: string;
}

const priorityColor = (priority: string): 'error' | 'warning' | 'default' => {
  switch (priority?.toUpperCase()) {
    case 'EMERGENCY': return 'error';
    case 'HIGH': return 'warning';
    default: return 'default';
  }
};

const statusColor = (status: string): 'info' | 'warning' | 'success' | 'default' => {
  switch (status?.toLowerCase()) {
    case 'waiting': return 'info';
    case 'in_service':
    case 'in-service':
    case 'called': return 'warning';
    case 'completed': return 'success';
    default: return 'default';
  }
};

const QueuePage = () => {
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await api.get(API_ENDPOINTS.QUEUE.ENTRIES("all"));
      setEntries(res.data || res || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch queue:', err);
      setError('Unable to load queue. Will retry.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const updateEntry = async (entry: QueueEntry, newStatus: string) => {
    setActionLoading(entry.id);
    try {
      await api.put(API_ENDPOINTS.QUEUE.UPDATE_ENTRY(String(entry.queue_id || "default"), String(entry.id)), { status: newStatus });
      await fetchQueue();
    } catch (err) {
      console.error('Failed to update entry:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const activeEntries = entries.filter((e) => e.status?.toLowerCase() !== 'completed');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Live Queue</Typography>
        <Box display="flex" gap={1} alignItems="center">
          <Typography variant="body2" color="textSecondary">Auto-refreshes every 30s</Typography>
          <Button variant="outlined" size="small" onClick={fetchQueue}>Refresh Now</Button>
        </Box>
      </Box>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
        <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
          <Typography variant="h5" color="primary">{activeEntries.length}</Typography>
          <Typography variant="body2" color="textSecondary">In Queue</Typography>
        </Paper>
        <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {entries.filter((e) => e.priority?.toUpperCase() === 'EMERGENCY').length}
          </Typography>
          <Typography variant="body2" color="textSecondary">Emergency</Typography>
        </Paper>
        <Paper sx={{ p: 2, minWidth: 120, textAlign: 'center' }}>
          <Typography variant="h5" color="warning.main">
            {entries.filter((e) => e.status?.toLowerCase() === 'in_service' || e.status?.toLowerCase() === 'in-service').length}
          </Typography>
          <Typography variant="body2" color="textSecondary">In Service</Typography>
        </Paper>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Wait (min)</TableCell>
              <TableCell>Vet</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">Queue is empty</TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  sx={{
                    bgcolor: entry.priority?.toUpperCase() === 'EMERGENCY' ? 'error.50' : 'inherit',
                  }}
                >
                  <TableCell><strong>{entry.queue_number}</strong></TableCell>
                  <TableCell>{entry.pet_name}</TableCell>
                  <TableCell>{entry.owner_name || '—'}</TableCell>
                  <TableCell>
                    <Chip
                      label={entry.priority || 'NORMAL'}
                      color={priorityColor(entry.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.status}
                      color={statusColor(entry.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{entry.wait_time != null ? entry.wait_time : '—'}</TableCell>
                  <TableCell>{entry.vet_name || '—'}</TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      {entry.status?.toLowerCase() === 'waiting' && (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={actionLoading === entry.id}
                          onClick={() => updateEntry(entry, 'called')}
                        >
                          Call
                        </Button>
                      )}
                      {(entry.status?.toLowerCase() === 'called' || entry.status?.toLowerCase() === 'waiting') && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          disabled={actionLoading === entry.id}
                          onClick={() => updateEntry(entry, 'in_service')}
                        >
                          In Service
                        </Button>
                      )}
                      {entry.status?.toLowerCase() !== 'completed' && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={actionLoading === entry.id}
                          onClick={() => updateEntry(entry, 'completed')}
                        >
                          Complete
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default QueuePage;
